import { FilterAltOffOutlined, FilterAltOutlined } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";

import { useQuery } from "@tanstack/react-query";
import { FormEvent, useEffect, useRef, useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import { Print } from "../../../Utilities/Print";
import { TableToExcel } from "../../../Utilities/TableToExcel";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import { Header } from "../../Header/Header";

type ReadingTarget = {
    id: number;
    meterNo: string;
    lastReading: number;
    property?: {
        customer?: {
            name?: string;
        };
    };
};

const fetchMeters = (params: any = {}, pagination: any) => {
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(
            ([, v]) => v !== "" && v !== undefined && v !== null
        )
    );
    return GET("/customer/meter/reading-targets", {
        ...cleanParams,
        page: pagination.page + 1,
        limit: pagination.limit,
    });
};

const safeName = (v?: string | null) =>
    v ? v.replace(/[\/\\:*?"<>|]/g, "_").replace(/\s+/g, "_") : "";

export const BlankReadingList = ({
    updateMode = false,
}: {
    updateMode?: boolean;
}) => {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    // filters
    const [clearAll, setClearAll] = useState({
        district: false,
        village: false,
        powerstation: false,
        hamlet: false,
        search: false,
    });
    const theme = useTheme();
    const [filter, setFilter] = useState({
        district: "",
        powerstation: "",
        distictName: "",
        villageName: "",
        hamletName: "",
        village: "",
        hamlet: "",
        meterNo: "",
        month: "",
        year: "",
    });

    // date as YYYY-MM (month picker)
    const [date, setDate] = useState("");

    const [fetchTargets, setFetchTargets] = useState(0);
    const [fetch, setFetch] = useState(false);
    const [district, setDistrict] = useState();
    const [village, setVillage] = useState();
    const [hamlet, setHamlet] = useState();
    const [powerstation, setPowerstation] = useState<any>();

    const [pagination, setPagination] = useState({
        page: 0,
        limit: 500,
        count: 0,
    });

    const handleFilter = () => {
        setFetch(!fetch);
    };

    // inputs per row
    const [fineValues, setFineValues] = useState<{ [id: number]: string }>({});
    const [currReadings, setCurrReadings] = useState<{ [id: number]: string }>(
        {}
    );
    const printRef = useRef<any>();

    const headerName = [
        district && `District: ${safeName(district)}`,
        powerstation && `Powerstation: ${safeName(powerstation)}`,
        village && `Village: ${safeName(village)}`,
        hamlet && `Hamlet: ${safeName(hamlet)}`,
    ]
        .filter(Boolean)
        .join(" | ");

    // For printing
    const componentRef = useRef<HTMLDivElement | any>(null);
    const [printMode, setPrintMode] = useState(false);
    // No need for printTriggerRef anymore since the Print component handles its own trigger button

    // *** New function to be called when "View Readings" is applied ***
    const onViewReadingsApplied = (
        currentFilter: typeof filter,
        selectedDate: string
    ) => {
        console.log("View Readings button was clicked!");
        console.log("Applied Filter:", currentFilter);
        console.log("Selected Billing Month:", selectedDate);
        // You can add any custom logic here:
        // - Send an analytics event
        // - Log the filter parameters
        // - Perform some other state updates not related to fetching
        // - Trigger a side effect
    };

    const handleClearAll = () => {
        // reset UI filters

        // reset applied filters
        setFilter({
            district: "",
            powerstation: "",
            distictName: "",
            villageName: "",
            hamletName: "",
            village: "",
            hamlet: "",
            meterNo: "",
            month: "",
            year: "",
        });
        setDate("");
        setFetchTargets((p) => p + 1);

        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            search: true,
            hamlet: true,
        });

        // reset search trigger & pagination

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    search: false,
                    hamlet: true,
                }),
            0
        );
    };

    // submit/search handler
    const handleApplyFilter = async (e: FormEvent) => {
        e.preventDefault();
        // extract month/year from date
        const [year, month] = date ? date.split("-") : ["", ""];
        // setFilter already contains region and meterNo
        setFilter((prev) => ({ ...prev, month, year }));
        // Increment to re-trigger query.
        setFetchTargets((p) => p + 1);

        // *** Call your custom function here after filter state is set ***
        // Pass the most up-to-date filter and date values
        onViewReadingsApplied({ ...filter, month, year }, date);
    };

    const getDistrict = async () => {
        if (!filter.district) return;
        const res = await GET("/region/district", { id: filter.district });
        const districtName =
            res?.data?.rows?.[0]?.name || res?.data?.rows?.[0]?.district?.name;
        if (districtName) setDistrict(districtName);
    };

    const getVillage = async () => {
        if (!filter.village) return;
        const res = await GET("/region/village", { id: filter.village });
        const villageName =
            res?.data?.rows?.[0]?.name || res?.data?.rows?.[0]?.village?.name;
        if (villageName) setVillage(villageName);
    };

    const getHamlet = async () => {
        if (!filter.hamlet) return;
        const res = await GET("/region/hamlet", { id: filter.hamlet });
        const hamletName =
            res?.data?.rows?.[0]?.name || res?.data?.rows?.[0]?.hamlet?.name;
        if (hamletName) setHamlet(hamletName);
    };

    const getPowerstation = async () => {
        if (!filter.powerstation) return;
        const res = await GET("/powerstation", { id: filter.powerstation });
        const powerstationName = res?.data?.rows?.[0]?.name || "";
        if (powerstationName) setPowerstation(powerstationName);
    };

    useEffect(() => {
        getDistrict();
        getVillage();
        getHamlet();
        getPowerstation();
    }, [filter.district, filter.village, filter.hamlet, filter.powerstation]);

    // query
    const { data, isFetching } = useQuery(
        ["reading-targets", fetchTargets, pagination],
        () => {
            // pass region filters + month/year + meterNo
            const params = {
                district: filter.district,
                village: filter.village,
                hamlet: filter.hamlet,
                meterNo: filter.meterNo,
                month: filter.month,
                year: filter.year,
                powerstation: filter.powerstation,
            };
            return fetchMeters(params, pagination);
        },
        {
            enabled: Boolean(fetchTargets), // only run after user chooses month
            networkMode: "always",
            retry: false,
        }
    );

    const fileName = [
        district && `District_${safeName(district)}`,
        village && `Village_${safeName(village)}`,
        hamlet && `Hamlet_${safeName(hamlet)}`,
        powerstation && `Powerstation_${safeName(powerstation)}`,
    ]
        .filter(Boolean)
        .join("__");

    useEffect(() => {}, [filter.month, filter.year]);

    return (
        <Box>
            <Typography variant="h5" sx={{ my: 2 }}>
                Blank Reading List
            </Typography>

            {!printMode && (
                <Box
                    component="form"
                    onSubmit={handleApplyFilter}
                    sx={{ mb: 2 }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <UpdatedSearchableInput
                                label="District"
                                api="/region/district"
                                filter={filter}
                                setFilter={(next) =>
                                    setFilter({ ...filter, ...next })
                                }
                                clearAll={clearAll.district}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                api="/powerstation"
                                label="Powerstation"
                                filter={filter}
                                setFilter={(next) =>
                                    setFilter({ ...filter, ...next })
                                }
                                dep={filter.district}
                                params={{
                                    district: filter.district,
                                }}
                                clearAll={clearAll.powerstation}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <UpdatedSearchableInput
                                label="Village"
                                api="/region/village"
                                filter={filter}
                                setFilter={(next) => {
                                    setFilter({ ...filter, ...next });
                                }}
                                dep={[filter.district, filter.powerstation]}
                                params={{
                                    district: filter.district,
                                    powerstation: filter.powerstation,
                                }}
                                clearAll={clearAll.village}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <UpdatedSearchableInput
                                label="Hamlet"
                                api="/region/hamlet"
                                filter={filter}
                                setFilter={(next) =>
                                    setFilter({ ...filter, ...next })
                                }
                                dep={[filter.village, filter.powerstation]}
                                params={{
                                    village: filter.village,
                                    powerstation: filter.powerstation,
                                }}
                                multiple={false}
                                clearAll={clearAll.hamlet}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                name="date"
                                label="Billing Month"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                required
                                type="month"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    max: currentMonth, // ✅ prevents forward months
                                    min: "2025-10",
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Button
                                onClick={handleFilter}
                                startIcon={<FilterAltOutlined />}
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                type="submit"
                            >
                                Search
                            </Button>
                        </Grid>

                        {/* CLEAR ALL */}
                        <Grid item xs={12} md={3}>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<FilterAltOffOutlined />}
                                onClick={handleClearAll}
                                sx={{
                                    height: "100%",
                                    minWidth: theme.spacing(20),
                                }}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>

                        {/* Print Button - Show only if data is available and not fetching */}
                    </Grid>
                </Box>
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                {!isFetching && data?.data?.rows?.length > 0 ? (
                    <div style={{ display: "flex" }}>
                        <Grid item xs={12} md={2}>
                            {/* Print component now directly renders its button */}

                            <Print
                                componentRef={componentRef}
                                setPrintMode={setPrintMode}
                                sx={{ width: "100%" }} // Adjust sx for the button directly
                            />
                        </Grid>

                        <Grid>
                            <TableToExcel
                                fileName={fileName || "Blank Reading List"}
                                sheetName={""}
                                targetRef={printRef}
                            />
                        </Grid>
                    </div>
                ) : (
                    <div> </div>
                )}

                <TablePagination
                    rowsPerPageOptions={[10, 50, 100, 500]}
                    component="div"
                    onPageChange={(ev, page) =>
                        setPagination({ ...pagination, page: page })
                    }
                    onRowsPerPageChange={(ev) =>
                        setPagination({
                            ...pagination,
                            limit: parseInt(ev.target.value),
                            page: 0,
                        })
                    }
                    count={data!?.data?.count ? data!?.data?.count : 0}
                    page={pagination.page}
                    rowsPerPage={pagination.limit}
                />
            </div>
            {/* Content to be printed */}
            <style>
                {`
@media print {
    @page {
        size: A4 portrait;
        /* Reduced page margins for more content space */
        margin: 10mm 8mm; /* top/bottom, left/right margins - more compact */
    }

    body {
        font-family: "Segoe UI", Arial, sans-serif;
        font-size: 11px; /* Slightly smaller font for compactness */
        color: #000;
        margin: 0;
        padding: 0;
        -webkit-print-color-adjust: exact;
    }

    .print-container {
        /* This class is not directly applied in your current JSX to a div wrapping content,
           but if you add it, these paddings would apply. Keeping it minimal. */
        padding: 5px 8px;
    }

    .report-header {
        text-align: center;
        /* Reduced margin below header */
        margin-bottom: 10px; /* More compact header spacing */
    }

    .report-header img {
        max-width: 80px; /* Slightly smaller logo if present */
        margin-bottom: 5px; /* Reduced spacing below logo */
    }

    table {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid #ccc; /* Slightly lighter border */
        /* Reduced margin above table */
        margin-top: 5px; /* More compact table spacing */
        table-layout: fixed;
    }

    th, td {
        border: 1px solid #ccc; /* Consistent border with table */
        /* Significantly reduced padding for a compact look */
        padding: 4px 6px; /* Reduced padding for more compactness */
        text-align: left;
        vertical-align: middle;
        word-wrap: break-word;
        white-space: normal;
    }

    th {
        /* Removed background color for a cleaner, ink-saving look */
        background-color: transparent; /* No background */
        font-weight: 700;
        text-align: center;
        /* Reduced padding for compactness */
        padding: 6px 8px; /* Reduced header padding */
    }

    td {
        font-size: 11px; /* Match body font size */
        /* Reduced line height for text compactness */
        line-height: 1.2; /* More compact line spacing */
    }

    tr:nth-child(even) {
        /* Removed background color for alternation to save ink and look cleaner */
        background-color: transparent; /* No alternating row background */
    }

    .summary-row {
        font-weight: bold;
        /* Removed background color */
        background-color: transparent; /* No background */
    }

    .print-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        text-align: center;
        font-size: 10px; /* Even smaller footer font */
        color: #555;
        padding: 5px 0; /* Reduced footer padding */
        border-top: 1px solid #ddd; /* Lighter border */
        background: white;
    }
}
`}
            </style>
            <Box
                ref={(el) => {
                    componentRef.current = el;
                    printRef.current = el;
                }}
            >
                {/* Loading */}
                {isFetching && (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                )}
                {/* Table */}
                {printMode && <Header name={headerName} />}
                {!isFetching && data?.data?.rows?.length > 0 && (
                    <TableContainer>
                        <Table
                            size="small"
                            ref={(el) => {
                                componentRef.current = el;
                                printRef.current = el;
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sr No.</TableCell>
                                    <TableCell>Customer Name</TableCell>
                                    <TableCell>Meter No</TableCell>
                                    <TableCell>Previous Reading</TableCell>
                                    <TableCell>Current Reading</TableCell>
                                    <TableCell>Fine</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data!.data.rows.map(
                                    (row: ReadingTarget, idx: number) => {
                                        const id = row.id;
                                        return (
                                            <TableRow key={id}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>
                                                    {row.property?.customer
                                                        ?.name ?? "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {row.meterNo}
                                                </TableCell>
                                                <TableCell>
                                                    {row.lastReading ?? "-"}
                                                </TableCell>

                                                {
                                                    <>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                disabled
                                                                value={
                                                                    fineValues[
                                                                        id
                                                                    ] ?? ""
                                                                }
                                                                onChange={(e) =>
                                                                    setFineValues(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [id]: e
                                                                                .target
                                                                                .value,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                disabled
                                                                value={
                                                                    currReadings[
                                                                        id
                                                                    ] ?? ""
                                                                }
                                                                onChange={(e) =>
                                                                    setCurrReadings(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [id]: e
                                                                                .target
                                                                                .value,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        </TableCell>
                                                    </>
                                                }
                                            </TableRow>
                                        );
                                    }
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* No data */}
                {!isFetching &&
                fetchTargets &&
                (!data?.data?.rows || data.data.rows.length === 0) ? (
                    <Alert severity="info">
                        We’re sorry, but no data was found.
                    </Alert>
                ) : null}
            </Box>
        </Box>
    );
};
